<script setup>
import { ref, computed, onMounted, watch, onUnmounted } from 'vue'
import apiClient from '@/api'
import AppLoader from '@/components/AppLoader.vue'
import Vue3EasyDataTable from 'vue3-easy-data-table'
import 'vue3-easy-data-table/dist/style.css'
import { webAlert, showConfirm } from '@/utils/dialog'
import SearchableSelectMakes from '@/components/SearchableSelectMakes.vue'
import SearchableSelectModels from '@/components/SearchableSelectModels.vue'


// --- STAN I DANE ---
const loading = ref(false)
const saving = ref(false)
const savingTXT = ref('')

const items = ref([])
const portals = ref([
    { ID: '1', name: 'Allegro', activeCount: 0, totalCount: 0 },
    { ID: '2', name: 'OtoMoto', activeCount: 0, totalCount: 0 },
    { ID: '3', name: 'OLX', activeCount: 0, totalCount: 0 },
    { ID: '4', name: 'Gratka', activeCount: 0, totalCount: 0 },
    { ID: '5', name: 'CarTeam', activeCount: 0, totalCount: 0 }
])

// Filtry
const selectedMakeId = ref(null)
const selectedModelId = ref(null)
const filterStatus = ref('')
const searchValue = ref('')
const vinSearch = ref('')

const serverItemsLength = ref(0)
const serverOptions = ref({
    page: 1,
    rowsPerPage: 10,
    sortBy: 'ID',
    sortType: 'desc'
})

// --- NAGŁÓWKI TABELI ---
const headers = computed(() => {
    const baseHeaders = [
        { text: 'ID', value: 'ID', sortable: true, width: 60 },
        { text: 'Pojazd', value: 'car_info', width: 220 }
    ]

    const portalHeaders = portals.value.map(p => ({
        text: p.name,
        value: `portal_${p.ID}`,
        width: 140
    }))

    return [...baseHeaders, ...portalHeaders]
})

// --- HELPER DO SPRAWDZANIA DATY ---
const isValidDate = (date) => {
    return date && date !== '0000-00-00' && date !== '0000-00-00 00:00:00'
}

// Mapowanie odpowiedzi API
const loadData = async () => {
    loading.value = true
    try {
        const response = await apiClient.post(
            {
                action: 'get-cars-publishing',
                page: serverOptions.value.page,
                limit: serverOptions.value.rowsPerPage,
                sortBy: serverOptions.value.sortBy,
                sortType: serverOptions.value.sortType,
                search: searchValue.value,
                vin: vinSearch.value,
                makeID: selectedMakeId.value,
                modelID: selectedModelId.value,
                status: filterStatus.value
            }
        )

        const rawItems = Array.isArray(response.data?.data) ? response.data.data : []

        items.value = rawItems.map(car => {
            const pubMap = {}
            if (Array.isArray(car.publications)) {
                car.publications.forEach(pub => {
                    const pid = pub.ID || pub.portalID
                    if (pid) {
                        pubMap[pid] = pub
                    }
                })
            } else if (car.publications && typeof car.publications === 'object') {
                Object.assign(pubMap, car.publications)
            }
            return {
                ...car,
                publications: pubMap
            }
        })

        serverItemsLength.value = response.data?.total || 0

        if (Array.isArray(response.data?.portals)) {
            portals.value = response.data.portals
        }
    } catch (e) {
        console.error(e)
    } finally {
        loading.value = false
    }
}

watch(serverOptions, () => { loadData() }, { deep: true })

let searchTimeout = null
const handleSearch = () => {
    clearTimeout(searchTimeout)
    searchTimeout = setTimeout(() => {
        serverOptions.value.page = 1
        loadData()
    }, 400)
}

const handleMakeChange = (make) => {
    selectedMakeId.value = make?.ID ?? make?.makeID ?? null
    selectedModelId.value = null
    loadData()
}

const handleModelChange = (model) => {
    selectedModelId.value = model?.ID ?? model?.modelID ?? null
    loadData()
}

// --- OBSŁUGA SELEKCJI I ZMIAN ---
// Mapa zaznaczonych komórek: { "carId_portalId": boolean }
const selectedCells = ref({})

const isPending = (status) => {
    return status === 'pending-active' || status === 'pending-deactive'
}

// Kliknięcie w komórkę – przełącza zaznaczenie (o ile nie jest pending)
const toggleCellSelection = (carId, portalId) => {
    const car = items.value.find(c => c.ID === carId)
    const status = car?.publications?.[portalId]?.status
    if (isPending(status)) return

    const key = `${carId}_${portalId}`
    selectedCells.value[key] = !selectedCells.value[key]
}

// Zaznaczanie całej kolumny portalu
const toggleAllForPortal = (portalId) => {
    const availableCars = items.value.filter(car => !isPending(car.publications?.[portalId]?.status))
    if (!availableCars.length) return

    const allSelected = availableCars.every(car => selectedCells.value[`${car.ID}_${portalId}`])
    availableCars.forEach(car => {
        selectedCells.value[`${car.ID}_${portalId}`] = !allSelected
    })
}

const isPortalAllSelected = (portalId) => {
    const availableCars = items.value.filter(car => !isPending(car.publications?.[portalId]?.status))
    if (!availableCars.length) return false
    return availableCars.every(car => selectedCells.value[`${car.ID}_${portalId}`])
}

// Licznik zaznaczonych pól
const selectedCount = computed(() => {
    return Object.values(selectedCells.value).filter(Boolean).length
})

const resetSelection = () => {
    selectedCells.value = {}
}

const formatDate = (dateStr) => {
    if (!dateStr || dateStr === '0000-00-00' || dateStr.startsWith('0000-00-00')) return null

    // Pobranie tylko części YYYY-MM-DD (na wypadek obecności godziny)
    const [datePart] = dateStr.split(' ')
    const parts = datePart.split('-')

    if (parts.length !== 3) return null

    const [year, month, day] = parts
    return `${day}.${month}.${year}`
}

// Wysyłka akcji zbiorczej do API
// Wysyłka akcji zbiorczej do API
const applyBatchAction = async (actionType) => {
    const actionName = actionType === 'publish' ? 'wystawić' : 'wycofać'
    const confirmed = await showConfirm(`Czy na pewno chcesz ${actionName} wybrane ogłoszenia (${selectedCount.value} szt.)?`, {
        title: 'Potwierdzenie akcji',
        confirmText: 'Wykonaj',
        cancelText: 'Anuluj',
        confirmClass: actionType === 'publish' ? 'btn-success' : 'btn-warning'
    })

    if (!confirmed) return

    saving.value = true
    savingTXT.value = 'Przetwarzanie ogłoszeń...'

    try {
        const payload = []
        items.value.forEach(car => {
            portals.value.forEach(p => {
                const key = `${car.ID}_${p.ID}`
                if (selectedCells.value[key]) {
                    payload.push({
                        carID: car.ID,
                        portalID: p.ID,
                        action: actionType // 'publish' lub 'withdraw'
                    })
                }
            })
        })

        const response = await apiClient.post(
            {
                action: 'batch-publish-cars',
                batchType: actionType, // <--- 'publish' lub 'withdraw'
                items: payload
            }
        )

        resetSelection()
        await loadData()
        webAlert(response.data)
    } catch {
        webAlert('Wystąpił błąd podczas wysyłania żądania.')
    } finally {
        saving.value = false
    }
}
onMounted(() => {
    loadData()
})

/*
watcher sprawdzajacy czy sie juz wyslalo
*/
const pollingTimer = ref(null)

// 1. Sprawdzanie czy którykolwiek element jest w trakcie aktywacji/deaktywacji
const hasPendingItems = computed(() => {
    return items.value.some(car => {
        if (!car.publications) return false
        return Object.values(car.publications).some(pub => isPending(pub?.status))
    })
})

// 2. Włączenie / wyłączenie odpytywania
const startPolling = () => {
    if (pollingTimer.value) return
    
    pollingTimer.value = setInterval(async () => {
        // Ciche pobranie bez aktywowania loader.value = true (ciche przeładowanie danych)
        await loadData(true) 
    }, 10000) // Odpytywanie co 10 sekundy
}

const stopPolling = () => {
    if (pollingTimer.value) {
        clearInterval(pollingTimer.value)
        pollingTimer.value = null
    }
}

// 3. Automatyczny reagowanie na zmiany w danych
watch(hasPendingItems, (newVal) => {
    if (newVal) {
        startPolling()
    } else {
        stopPolling()
    }
}, { immediate: true })

// Czyść timer przy wyjściu z widoku
onUnmounted(() => {
    stopPolling()
})
</script>
<template>
    <div class="container-fluid pb-5">
        <!-- Nagłówek -->
        <div
            class="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3 mb-4 px-3 px-md-0">
            <div>
                <h3 class="fw-bold mb-1">Multiwystawianie ogłoszeń</h3>
                <p class="text-muted small mb-0">Publikacja oraz masowa zmiana statusów ofert na serwisach zewnętrznych
                </p>
            </div>
        </div>

        <!-- Filtry -->
        <div class="card border-0 shadow-sm mb-4">
            <div class="card-body">
                <div class="row g-2 mb-3">
                    <div class="col-12 col-md-2">
                        <SearchableSelectMakes v-model="selectedMakeId" placeholder="Wybierz markę"
                            @change="handleMakeChange" />
                    </div>
                    <div class="col-12 col-md-2">
                        <SearchableSelectModels v-model="selectedModelId" :make-id="selectedMakeId"
                            placeholder="Wybierz model" @change="handleModelChange" />
                    </div>
                    <div class="col-12 col-md-2">
                        <select class="form-select" v-model="filterStatus" @change="loadData">
                            <option value="">Wszystkie statusy</option>
                            <option value="active">Wystawione</option>
                            <option value="inactive">Niewystawione</option>
                        </select>
                    </div>
                    <div class="col-12 col-md-3">
                        <input v-model="vinSearch" @input="handleSearch" type="text" class="form-control"
                            placeholder="Szukaj VIN / Nr..." />
                    </div>
                    <div class="col-12 col-md-3">
                        <input v-model="searchValue" @input="handleSearch" type="text" class="form-control"
                            placeholder="Szukaj (Marka, Model)..." />
                    </div>
                </div>

                <div class="table-responsive w-100">
                    <Vue3EasyDataTable v-model:server-options="serverOptions" :headers="headers" :items="items"
                        :loading="loading" :server-items-length="serverItemsLength" buttons-pagination
                        class="table-font-13" table-class-name="table table-hover align-middle custom-easy-table"
                        :rows-items="[10, 25, 50, 100]" empty-message="Brak pojazdów w systemie"
                        rows-per-page-message="Wierszy na stronę:">

                        <!-- Pojazd / Cena / Fotka -->
                        <template #item-car_info="item">
                            <div class="d-flex align-items-center gap-3 py-1">
                                <div class="car-thumb border rounded bg-light flex-shrink-0">
                                    <img v-if="item.image"
                                        :src="`https://integrator.wrealizacji.pl/photos/${item.ID}/thumb/${item.image}`"
                                        class="w-100 h-100 object-fit-cover rounded" />
                                    <i v-else class="bi bi-car-front text-secondary fs-4"></i>
                                </div>
                                <div>
                                    <div class="fw-bold ">{{ item.make }} {{ item.model }}</div>
                                    <div class="text-primary fw-semibold small">{{ Number(item.price).toLocaleString()
                                        }} PLN</div>
                                    <div class="extra-small text-muted font-monospace">{{ item.VIN || 'Brak VIN' }}
                                    </div>
                                </div>
                            </div>
                        </template>

                        <!-- Nagłówki kolumn portali (Zaznacz wszystko + licznik) -->
                        <template v-for="p in portals" #[`header-portal_${p.ID}`]>
                            <div :key="'h_' + p.ID" class="portal-header-cell text-center py-1">
                                <div class="form-check d-flex align-items-center justify-content-center gap-1 mb-1 cursor-pointer"
                                    @click.stop="toggleAllForPortal(p.ID)">
                                    <input class="form-check-input cursor-pointer" type="checkbox"
                                        :checked="isPortalAllSelected(p.ID)" />
                                    <span class="extra-small text-muted fw-normal">Zaznacz</span>
                                </div>
                                <div class="fw-bold ">{{ p.name }}</div>
                                <div class="extra-small mt-1">
                                    <span class="text-success fw-bold">{{ p.activeCount }}</span> / <span
                                        class="text-muted">{{ p.totalCount }}</span>
                                </div>
                            </div>
                        </template>

                        <!-- Komórki statusów portali -->
                        <!-- Komórki statusów portali -->
                        <template v-for="p in portals" #[`item-portal_${p.ID}`]="item">
                            <div :key="'i_' + p.ID"
                                class="portal-cell-wrapper p-2 rounded transition-all cursor-pointer user-select-none"
                                :class="{
                                    'cell-selected': selectedCells[`${item.ID}_${p.ID}`],
                                    'cell-disabled': isPending(item.publications?.[p.ID]?.status)
                                }" @click="toggleCellSelection(item.ID, p.ID)">

                                <!-- Informacja o statusie w bazie -->
                                <div
                                    class="status-indicator extra-small d-flex flex-column align-items-center gap-1 text-center">

                                    <!-- 1. W TRAKCIE AKTYWACJI -->
                                    <div v-if="item.publications?.[p.ID]?.status === 'pending-active'"
                                        class="d-flex align-items-center gap-1 text-warning fw-semibold py-1">
                                        <span class="spinner-border spinner-border-sm"
                                            style="width: 11px; height: 11px;"></span>
                                        <span>W trakcie aktywacji</span>
                                    </div>

                                    <!-- 2. W TRAKCIE DEAKTYWACJI -->
                                    <div v-else-if="item.publications?.[p.ID]?.status === 'pending-deactive'"
                                        class="d-flex align-items-center gap-1 text-danger fw-semibold py-1">
                                        <span class="spinner-border spinner-border-sm"
                                            style="width: 11px; height: 11px;"></span>
                                        <span>W trakcie deaktywacji</span>
                                    </div>

                                    <!-- 3. AKTYWNE -->
                                    <div v-else-if="item.publications?.[p.ID]?.status === 'active'"
                                        class="d-flex flex-column align-items-center">
                                        <div class="d-flex align-items-center gap-1 text-success fw-bold">
<i class="bi bi-check-circle-fill text-success fs-6"></i>                                            <span>Aktywne</span>
                                        </div>
                                        <span v-if="isValidDate(item.publications[p.ID].expiredDate)"
                                            class="text-muted font-monospace extra-small">
                                            do {{ formatDate(item.publications[p.ID].expiredDate) }} </span>
                                    </div>

                                    <!-- 4. NIEAKTYWNE / SZKIC -->
                                    <div v-else class="d-flex align-items-center gap-1 text-muted py-1">
                                        <span>Nieaktywne</span>
                                    </div>

                                    <!-- ID zewnętrznego ogłoszenia / Link (stopPropagation zapobiega zaznaczaniu komórki przy kliknięciu w link) -->
                                    <template v-if="item.publications?.[p.ID]?.returnAdvertID">
                                        <a v-if="item.publications[p.ID].returnAdvertURL"
                                            :href="item.publications[p.ID].returnAdvertURL" target="_blank" @click.stop
                                            rel="noopener noreferrer"
                                            class="text-decoration-none font-monospace extra-small link-primary mt-1"
                                            title="Otwórz ogłoszenie">
                                            ID: {{ item.publications[p.ID].returnAdvertID }} <i
                                                class="bi bi-box-arrow-up-right ms-1"></i>
                                        </a>
                                        <span v-else class="text-muted font-monospace extra-small mt-1">
                                            ID: {{ item.publications[p.ID].returnAdvertID }}
                                        </span>
                                    </template>

                                </div>
                            </div>
                        </template>
                    </Vue3EasyDataTable>
                </div>
            </div>
        </div>

        <!-- Floating Action Toolbar -->
        <!-- Floating Action Toolbar -->
        <Transition name="slide-up">
            <div v-if="selectedCount > 0"
                class="floating-toolbar shadow-lg rounded-pill bg-dark text-white px-4 py-3 d-flex align-items-center justify-content-between gap-4">
                <div class="d-flex align-items-center gap-2">
                    <span class="badge bg-primary rounded-circle p-2 fs-6">{{ selectedCount }}</span>
                    <span class="fw-medium">Zaznaczonych pozycji</span>
                </div>
                <div class="d-flex align-items-center gap-2">
                    <button class="btn btn-outline-light btn-sm rounded-pill px-3" @click="resetSelection">Odznacz
                        wszystkie</button>
                    <button class="btn btn-warning btn-sm rounded-pill px-3 fw-bold"
                        @click="applyBatchAction('withdraw')">
                        <i class="bi bi-x-circle me-1"></i> Wycofaj zaznaczone
                    </button>
                    <button class="btn btn-success btn-sm rounded-pill px-3 fw-bold"
                        @click="applyBatchAction('publish')">
                        <i class="bi bi-check-circle me-1"></i> Wystaw zaznaczone
                    </button>
                </div>
            </div>
        </Transition>
    </div>

    <div id="glownyloader">
        <AppLoader :show="saving" :text="savingTXT" />
    </div>
</template>

<style scoped>
.car-thumb {
    width: 60px;
    height: 42px;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
}

.portal-header-cell {
    min-width: 110px;
    width: 100% !important;
}

.extra-small {
    font-size: 0.75rem;
}

/* Custom Switch */
.custom-switch {
    position: relative;
    display: inline-block;
    width: 38px;
    height: 20px;
}

.custom-switch input {
    opacity: 0;
    width: 0;
    height: 0;
}

.slider {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: var(--switch-bg);
    transition: .3s;
}

.slider:before {
    position: absolute;
    content: "";
    height: 14px;
    width: 14px;
    left: 3px;
    bottom: 3px;
    background-color: var(--switch-handle-bg);
    transition: .3s;
}

input:checked+.slider {
    background-color: var(--switch-active-bg);
}

input:focus+.slider {
    box-shadow: 0 0 1px var(--switch-active-bg);
}

input:checked+.slider:before {
    transform: translateX(18px);
}

.slider.round {
    border-radius: 20px;
}

.slider.round:before {
    border-radius: 50%;
}

.status-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    display: inline-block;
}

.floating-toolbar {
    position: fixed;
    bottom: 25px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 1040;
    min-width: 500px;
    max-width: 90vw;
    background-color: var(--toolbar-bg);
    border: 1px solid var(--toolbar-border);
    box-shadow: 0 10px 25px -5px var(--toolbar-shadow);
}

.slide-up-enter-active,
.slide-up-leave-active {
    transition: all 0.3s ease-out;
}

.slide-up-enter-from,
.slide-up-leave-to {
    transform: translate(-50%, 100px);
    opacity: 0;
}

.table-responsive {
    display: block;
    width: 100%;
    overflow-x: auto;
    max-width: calc(100vw - 2rem) !important;
    -webkit-overflow-scrolling: touch;
}

.portal-cell-wrapper {
    border: 2px solid transparent;
    min-height: 58px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.portal-cell-wrapper:hover:not(.cell-disabled) {
    background-color: var(--cell-hover-bg);
}

/* Stan Zaznaczenia Komórki */
.portal-cell-wrapper.cell-selected {
    background-color: var(--cell-selected-bg) !important;
    border-color: var(--cell-selected-border) !important;
    box-shadow: inset 0 0 0 1px var(--cell-selected-border);
}

/* Zablokowana Komórka (pending) */
.portal-cell-wrapper.cell-disabled {
    opacity: 0.6;
    cursor: not-allowed;
    background-color: var(--cell-disabled-bg);
}

.transition-all {
    transition: all 0.15s ease-in-out;
}

:deep(.vue3-easy-data-table) {
    --easy-table-border: 1px solid var(--table-border-color);
    --easy-table-row-border: 1px solid var(--table-border-color);
    --easy-table-header-font-size: 0.85rem;
    --easy-table-header-height: 45px;
    --easy-table-body-row-height: 50px;

    width: 100% !important;
    max-width: 100% !important;
}

:deep(.vue3-easy-data-table__main) {
    width: 100% !important;
    overflow-x: auto !important;
}
</style>